/**
 * AlertBanner — banner de estado/degradado del sistema (tema dark "torre de control").
 * Usado para: circuit-breaker visual (ARIBA/CERTRONIC caídos), avisos de vencimiento, errores globales.
 * Variantes semánticas con fondo *-tint + ícono del sprite (nunca caracteres sueltos ni color solo:
 * el ícono y el título/mensaje siempre acompañan al color).
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { IconComponent } from '../../atoms/icon/icon.component';

export type AlertType = 'info' | 'success' | 'warning' | 'error';

interface AlertConfig {
  containerClass: string;
  iconClass: string;
  icon: string;
}

const ALERT_CONFIG: Record<AlertType, AlertConfig> = {
  info: {
    containerClass: 'border-info/30 bg-info-tint',
    iconClass: 'text-info',
    icon: 'bell',
  },
  success: {
    containerClass: 'border-success/30 bg-success-tint',
    iconClass: 'text-success',
    icon: 'check',
  },
  warning: {
    containerClass: 'border-warning/30 bg-warning-tint',
    iconClass: 'text-warning',
    icon: 'alert-triangle',
  },
  error: {
    containerClass: 'border-danger/30 bg-danger-tint',
    iconClass: 'text-danger',
    icon: 'alert-triangle',
  },
};

@Component({
    selector: 'pvp-alert-banner',
    imports: [IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div
      role="alert"
      [class]="containerClass()"
    >
      <span class="mt-0.5 shrink-0" [class]="iconClass()" aria-hidden="true">
        <pvp-icon [name]="alertIcon()" size="sm" />
      </span>

      <div class="flex-1 min-w-0">
        @if (title()) {
          <p class="m-0 text-sm font-semibold text-text">{{ title() }}</p>
        }
        @if (message()) {
          <p class="m-0 text-sm text-text-muted">{{ message() }}</p>
        }
        <ng-content />
      </div>

      @if (dismissible()) {
        <button
          type="button"
          class="shrink-0 rounded-sm p-1 text-text-muted transition-colors hover:bg-surface-raised hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Cerrar"
          (click)="dismissed.emit()"
        >
          <pvp-icon name="x" size="sm" />
        </button>
      }
    </div>
  `
})
export class AlertBannerComponent {
  readonly type        = input<AlertType>('info');
  readonly title       = input<string>('');
  readonly message     = input<string>('');
  readonly dismissible = input(false);
  /** Override del ícono del sprite; vacío = ícono default de la variante. */
  readonly icon        = input<string>('');

  readonly dismissed = output<void>();

  protected containerClass(): string {
    return (
      'flex items-start gap-3 rounded-md border p-4 text-sm ' +
      ALERT_CONFIG[this.type()].containerClass
    );
  }

  protected iconClass(): string {
    return ALERT_CONFIG[this.type()].iconClass;
  }

  protected alertIcon(): string {
    return this.icon() || ALERT_CONFIG[this.type()].icon;
  }
}
